import { Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import { ProductImageValidationError } from '../domain/productImageErrors';

export { ProductImageValidationError, isProductImageValidationError } from '../domain/productImageErrors';

export type PickedProductImage = {
  uri: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  base64: string;
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

function isAllowedMime(mime: string): boolean {
  return mime.startsWith('image/');
}

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error('No se pudo leer la imagen seleccionada.');
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('No se pudo procesar la imagen.'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer la imagen.'));
      reader.readAsDataURL(blob);
    });
  }
  return FileSystem.readAsStringAsync(uri, { encoding: 'base64' as const });
}

async function pickProductImageNative(): Promise<PickedProductImage | null> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    quality: 1.0,
    base64: true,
    allowsEditing: false,
  });

  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }

  const asset = result.assets[0];
  const mimeType = asset.mimeType ?? 'image/jpeg';
  const fileSize = asset.fileSize ?? 0;

  if (!isAllowedMime(mimeType)) {
    throw new ProductImageValidationError('Solo se admiten imágenes (JPG, PNG, WebP).');
  }
  if (fileSize > MAX_FILE_SIZE) {
    throw new ProductImageValidationError('La imagen no puede superar los 5 MB.');
  }

  const base64 = asset.base64 ?? (await readFileAsBase64(asset.uri));
  return {
    uri: asset.uri,
    fileName: asset.fileName ?? 'producto.jpg',
    mimeType,
    fileSize,
    base64,
  };
}

function pickProductImageWebInput(): Promise<PickedProductImage | null> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    Object.assign(input.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      overflow: 'hidden',
    });

    let settled = false;
    const finish = (value: PickedProductImage | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        finish(null);
        return;
      }
      try {
        const mimeType = file.type || 'image/jpeg';
        if (!isAllowedMime(mimeType)) {
          reject(new ProductImageValidationError('Solo se admiten imágenes (JPG, PNG, WebP).'));
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          reject(new ProductImageValidationError('La imagen no puede superar los 5 MB.'));
          return;
        }
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result;
            if (typeof result === 'string') {
              const commaIdx = result.indexOf(',');
              res(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
            } else {
              rej(new Error('No se pudo procesar la imagen.'));
            }
          };
          reader.onerror = () => rej(new Error('Error al leer la imagen.'));
          reader.readAsDataURL(file);
        });
        finish({
          uri: URL.createObjectURL(file),
          fileName: file.name,
          mimeType,
          fileSize: file.size,
          base64,
        });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    input.oncancel = () => finish(null);

    document.body.appendChild(input);
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.click();
      }
    } catch {
      input.click();
    }
  });
}

export async function pickProductImage(): Promise<PickedProductImage | null> {
  if (Platform.OS === 'web') {
    return pickProductImageWebInput();
  }
  return pickProductImageNative();
}
